import { Check } from "lucide-react-native";
import { useState } from "react";
import { Box } from "../../../../ui/box";
import { Button, ButtonText } from "../../../../ui/button";
import { HStack } from "../../../../ui/hstack";
import { Icon } from "../../../../ui/icon";
import { Text } from "../../../../ui/text";
import { VStack } from "../../../../ui/vstack";
import { AGENT_ACTIONS } from "../../actions";
import type { AgentChatMessage } from "../../types";
import { useAgentForm } from "./AgentFormContext";
import FormAnswer from "./FormAnswer";
import FormCardFrame from "./FormCardFrame";
import { canEditBrief } from "./formFlow";
import type { FormId } from "./formTypes";
import { parseSummaryLines } from "./formValues";

const TITLES: Record<FormId, string> = {
  BRIEF: "Brief enviado",
  LOGISTICS: "Detalles enviados",
};

type Props = {
  /** A form row that carries its submission: both `form` and `formAnswer` are set. */
  message: AgentChatMessage;
};

/**
 * A submitted card, collapsed to what was decided.
 *
 * The summary is the row's own text — the backend writes it as `Label: value` lines,
 * with the names the values alone do not carry (a product id says nothing; its name
 * does). "Editar brief" re-opens the same card in place, prefilled, for as long as the
 * session still accepts it (no concept chosen yet) and only on the newest submission; after that the Brief is
 * history and changes go through the chat.
 */
const SubmittedForm = ({ message }: Props) => {
  const { flow, busy, can } = useAgentForm();
  const [editing, setEditing] = useState(false);
  const { form, formAnswer } = message;
  if (!form || !formAnswer) return null;

  const { formId, values } = formAnswer;
  const editable =
    formId === "BRIEF" &&
    canEditBrief(flow, message.id, can(AGENT_ACTIONS.submitBrief));

  if (editing && editable) {
    return (
      <FormAnswer
        card={form}
        initial={values}
        onCancel={() => setEditing(false)}
      />
    );
  }

  const lines = parseSummaryLines(message.text);

  return (
    <FormCardFrame label={TITLES[formId]}>
      <VStack space="sm">
        <HStack space="xs" className="items-center">
          <Icon as={Check} size="sm" className="text-success-600" />
          <Text className="text-sm font-semibold text-typography-900">
            {TITLES[formId]}
          </Text>
        </HStack>
        <VStack space="xs">
          {lines.map((line, index) => (
            // Label above value on a phone: a fixed label column there leaves the
            // value a word per line.
            <Box
              key={`${line.label ?? ""}-${index}`}
              className="flex-col gap-0.5 sm:flex-row sm:items-start sm:gap-2"
            >
              {line.label ? (
                <Text className="shrink-0 text-xs text-typography-500 sm:w-40">
                  {line.label}
                </Text>
              ) : null}
              <Box className="min-w-0 sm:flex-1">
                <Text className="text-sm text-typography-800">
                  {line.value}
                </Text>
              </Box>
            </Box>
          ))}
        </VStack>
        {editable ? (
          <Button
            action="brand"
            variant="link"
            size="sm"
            className="min-h-11 self-start"
            isDisabled={busy}
            onPress={() => setEditing(true)}
          >
            <ButtonText>Editar brief</ButtonText>
          </Button>
        ) : null}
      </VStack>
    </FormCardFrame>
  );
};

export default SubmittedForm;
